import React, { useState, useEffect } from "react";
import { Calendar } from "../Components/UI/calendar";
import { Button } from "../Components/UI/Button";
import { Input } from "../Components/UI/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Components/UI/popover";
import { CalendarIcon, X } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { IncidentQueryField } from "../types/salesforce";

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  field: IncidentQueryField;
  operator: string;
}

const FORMAT_STRING = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
const DISPLAY_FORMAT = "MMM d, yyyy h:mm a";
const DATE_ONLY_FORMAT = "MMM d, yyyy";

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  field,
  operator,
}) => {
  const isDateTime = field.Field_Type__c === "DateTime";
  const [date, setDate] = useState<Date | undefined>(
    value ? parse(value, FORMAT_STRING, new Date()) : undefined
  );
  const [time, setTime] = useState(date ? format(date, "HH:mm") : "00:00");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (date) {
      let newDate = date;
      if (isDateTime) {
        const [hours, minutes] = time.split(":").map(Number);
        newDate = new Date(date.setHours(hours, minutes, 0, 0));
      }
      onChange(format(newDate, FORMAT_STRING));
    }
  }, [date, time, isDateTime, onChange]);

  const handleSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    if (!isDateTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTime(e.target.value);
  };

  const handleClear = () => {
    setDate(undefined);
    setTime("00:00");
    onChange("");
    setIsOpen(false);
  };

  const formatDisplayValue = () => {
    if (!date) return "";
    return isDateTime
      ? format(date, DISPLAY_FORMAT)
      : format(date, DATE_ONLY_FORMAT);
  };

  return (
    <div className="flex gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-full justify-start text-left font-normal ${
              !date && "text-muted-foreground"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDisplayValue() ||
              (isDateTime ? "Select date and time" : "Select date")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            initialFocus
          />
          {isDateTime && (
            <div className="border-t p-3">
              <Input
                type="time"
                value={time}
                onChange={handleTimeChange}
                className="w-full"
              />
            </div>
          )}
        </PopoverContent>
      </Popover>

      {date && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="text-red-500"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {/* Manual input option */}
      <Input
        type={isDateTime ? "datetime-local" : "date"}
        value={value ? value.slice(0, 16) : ""} // Trim milliseconds and timezone
        onChange={(e) => {
          const inputDate = new Date(e.target.value);
          if (isValid(inputDate)) {
            setDate(inputDate);
            if (isDateTime) {
              setTime(format(inputDate, "HH:mm"));
            }
          }
        }}
        className="hidden" // Hidden by default but can be shown if needed
      />
    </div>
  );
};
